import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Observable, of } from 'rxjs';
import { Action, Store } from '@ngrx/store';
import { AppointmentService } from './../../shared/services/appointment.service';
import * as fromActions from './sales-appointment.actions';
import * as fromAdapter from './sales-appointment.adapter';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { SalesAppointmentState, SeriesDetailsForYear } from './sales-appointment.reducer';
import { stateDetails } from './sales-appointment.selectors';
import { selectDealerAddressLong} from '../dealer/dealer.selectors';
import { Injectable, Injector } from '@angular/core';
import { VehicleService } from '../../shared/services/vehicle.service';
import { VehicleColor } from './../../shared/models/series-data';
import { IResponse, IAvailabilityOptions, IAvailability, ITestDriveVehicleInventory } from '@signal/asp-data-commons';
import { AvailabilityService } from './../../shared/services/availability.service';
import {toData} from '../app.adapter';
import { GLOBAL_CONSTANT, getAlertErrorMessage } from '../../shared/services/util.service';
import { AlertService } from '../../shared/services/alert.service';
import { DealerState } from '../dealer/dealer.reducer';
import { IAddress } from '@signal/asp-data-commons/lib/interfaces/appointment';
import { VehiclesState } from '../vehicle/vehicle.reducer';
import { selectAllVehiclesFromInventory } from '../vehicle/vehicle.selectors';
import { CustomerService } from '../../shared/services/customer.service';

@Injectable()
export class SalesAppointmentEffects {
    
    createAppointment: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.CreateSalesAppointment>(
            fromActions.SalesAppointmentActionTypes.CREATE_SALES_APPOINTMENT
        ),
        withLatestFrom(this.store$.select(stateDetails),this.dealerState.select(selectDealerAddressLong), this.vehicleState.select(selectAllVehiclesFromInventory)),
        switchMap(([action, state,dealerAddress, allTestDriveVehicles]: [fromActions.CreateSalesAppointment, SalesAppointmentState, IAddress, ITestDriveVehicleInventory[]]) =>
            this.appointmentService.create(fromAdapter.createSalesAppointment(state,action.payload,dealerAddress, allTestDriveVehicles)).pipe(
                map((appointmentModel) => {
                    if(action.payload.from === 'sendLink'){
                        this.alertService.showAlert(`Link sent to customer successfully`, '', '', 5000, false);
                        return new fromActions.SendLinkToCustomer({ appointmentIds:[appointmentModel.data.appointmentId],appointment:appointmentModel.data});
                    }else{
                        return new fromActions.CreateSalesAppointmentSuccess({ appointmentModel: appointmentModel.data })
                    }
                    }),
                    catchError(err => this.handleRetryForCreate(action, err))
            )
        )
    ));

    
    getEditData: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.GetEditData>(
            fromActions.SalesAppointmentActionTypes.GET_EDIT_DATA
        ),
        mergeMap((action: fromActions.GetEditData) =>
            this.appointmentService.get(action.payload.appointmentType, action.payload.appointmentId).pipe(
                map((appointmentModel) => new fromActions.GetEditDataSuccess({ appointmentModel: appointmentModel.data })),
                catchError(err => of(new fromActions.GetEditDataFail({ error: err })))
            ))));
    
    
    sendLinkToCustomer: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.SendLinkToCustomer>(
            fromActions.SalesAppointmentActionTypes.SEND_LINK_TO_CUSTOMER
        ),
        mergeMap((action: fromActions.SendLinkToCustomer) =>
            this.appointmentService.sendLink(action.payload.appointmentIds).pipe(
                map((value) => new fromActions.CreateSalesAppointmentSuccess({appointmentModel: action.payload.appointment})),
                catchError(err => of(new fromActions.SendLinkToCustomerFailure({ error: err })))
            ))));

    
    editAppointment: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.EditSalesAppointment>(
            fromActions.SalesAppointmentActionTypes.EDIT_SALES_APPOINTMENT
        ),
        withLatestFrom(this.store$.select(stateDetails),this.dealerState.select(selectDealerAddressLong), this.vehicleState.select(selectAllVehiclesFromInventory)),
        switchMap(([action, state, dealerAddress, allTestDriveVehicles]: [fromActions.EditSalesAppointment, SalesAppointmentState, IAddress, ITestDriveVehicleInventory[]]) =>
            this.appointmentService.update(fromAdapter.updateSalesAppointment(state,action.payload,dealerAddress, allTestDriveVehicles)).pipe(
                map((appointmentModel) => new fromActions.EditSalesAppointmentSuccess(
                    { appointmentModel: appointmentModel.data })),
                    catchError(err => this.handleRetryForEdit(action, err))
            )
        )
    ));

    
    loadSeriesAndTrim: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadSeriesAndTrim>(
            fromActions.SalesAppointmentActionTypes.LOAD_SERIES_TRIM
        ),
        mergeMap((action: fromActions.LoadSeriesAndTrim) =>
            this.vehicleService.getSeriesData('TOY').pipe(
                map(value => fromAdapter.getSeriesAndTrim(value)),
                map((seriesDetails: SeriesDetailsForYear[]) => new fromActions.LoadSeriesAndTrimSuccess({ seriesDetails })),
                catchError(err => of(new fromActions.LoadSeriesAndTrimFail({ error: err })))
            )
        )
    ));

    
    loadExteriorColors: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadExteriorColors>(
            fromActions.SalesAppointmentActionTypes.LOAD_EXTERIOR_COLORS
        ),
        mergeMap((action: fromActions.LoadExteriorColors) =>
            this.vehicleService.getVehicleColors('TOY', action.payload.year, action.payload.model, action.payload.trim).pipe(
                map(value => fromAdapter.getExteriorColors(value)),
                map((exteriorColors: VehicleColor[]) => new fromActions.LoadExteriorColorsSuccess({ exteriorColors })),
                catchError(err => of(new fromActions.LoadExteriorColorsFail({ error: err })))
            )
        )
    ));

    
    loadAvailabilityOptions: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadAvailabilityOptions>(
            fromActions.SalesAppointmentActionTypes.LOAD_AVAILABILITY_OPTIONS
        ),
        mergeMap((action: fromActions.LoadAvailabilityOptions) =>
            this.availabilityService.getAvailabilityOptions('sales', action.payload.appointmentType).pipe(
                switchMap((data: IResponse<IAvailabilityOptions>) => toData(data)),
                map((availabilityOptions: IAvailabilityOptions) => new fromActions.LoadAvailabilityOptionsSuccess({ availabilityOptions })),
                catchError(err => of(new fromActions.LoadAvailabilityOptionsFail({ error: err })))
            )
        )
    ));

    
    loadAvailability: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadAvailability>(
            fromActions.SalesAppointmentActionTypes.LOAD_AVAILABILITY
        ),
        mergeMap((action: fromActions.LoadAvailability) =>
            this.availabilityService.getAvailability('sales', action.payload).pipe(
                switchMap((data: IResponse<IAvailability>) => toData(data, 201)),
                map((availability: IAvailability) => 
                    new fromActions.LoadAvailabilitySuccess({ availability, loadPayload: action.payload })),
                catchError(err => of(new fromActions.LoadAvailabilityFail({ error: err })))
            )
        )
    ));
    
    loadTestDriveVinVehicle: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadTestDriveVinVehicle>(
            fromActions.SalesAppointmentActionTypes.LOAD_TESTDRIVE_VIN_VEHICLE
        ),
        mergeMap((action: fromActions.LoadTestDriveVinVehicle) =>
            this.vehicleService.getVehicleColorForLexus(action.payload.vehicle,true).pipe(
                switchMap((data: IResponse<any>) => toData(data, 201)),
                map((vehicle: any) => new fromActions.LoadTestDriveVinVehiclecSuccess({ data:vehicle })),
                catchError(err => of(new fromActions.LoadTestDriveVinVehiclecFailure({ error: err })))
            )
        )
    ));

    loadSalesAppointmentFromSignalDeepLink: Observable<Action> = createEffect(() => this.actions$.pipe(
        ofType<fromActions.LoadSalesAppointmentFromSignalDeepLink>(
          fromActions.SalesAppointmentActionTypes.LOAD_SALES_APPOINTMENT_FROM_SIGNAL_DEEP_LINK
        ),
        mergeMap((action: fromActions.LoadSalesAppointmentFromSignalDeepLink) => {
          const payload = action instanceof fromActions.LoadSalesAppointmentFromSignalDeepLink ? action.payload : undefined;
          return this.customerService.getApptBySignalDeepLink(payload.signalDeeplinkId).pipe(
            map((data) => new fromActions.LoadSalesAppointmentFromSignalDeepLinkSuccess({ data })),
            catchError(err => of(new fromActions.LoadSalesAppointmentFromSignalDeepLinkFail({ error: err })))
          )
        }
        )
      ));
    
    vehicleService:VehicleService;
    alertService: AlertService;
    availabilityService: AvailabilityService;
    appointmentService: AppointmentService;
    customerService: CustomerService;

    constructor(
        injector : Injector,
        private actions$: Actions,
        private store$: Store<SalesAppointmentState>,
        private readonly dealerState: Store<DealerState>,
        private readonly vehicleState: Store<VehiclesState>
    ) {
        this.vehicleService = injector.get<VehicleService>(VehicleService);
        this.alertService = injector.get<AlertService>(AlertService);
        this.availabilityService = injector.get<AvailabilityService>(AvailabilityService);
        this.appointmentService = injector.get<AppointmentService>(AppointmentService);
        this.customerService = injector.get<CustomerService>(CustomerService);
    }

    private handleRetryForCreate(action: fromActions.CreateSalesAppointment, err: any): Observable<Action> {
        const attempt = action.payload?.retryAttempt || 1;
        if (attempt >= 3) {
            this.alertService.showEscalationAlert();
            return of(new fromActions.CreateSalesAppointmentFail({ error: err }));
        }

        const errorMessage = getAlertErrorMessage(err, GLOBAL_CONSTANT.errorMessage);
        return this.alertService.openRetryPopup(errorMessage).pipe(
            map((shouldRetry: boolean) => shouldRetry
                ? new fromActions.CreateSalesAppointment({
                    ...action.payload,
                    retryAttempt: attempt + 1
                })
                : new fromActions.CreateSalesAppointmentFail({ error: err }))
        );
    }

    private handleRetryForEdit(action: fromActions.EditSalesAppointment, err: any): Observable<Action> {
        const attempt = action.payload?.retryAttempt || 1;
        if (attempt >= 3) {
            this.alertService.showEscalationAlert();
            return of(new fromActions.EditSalesAppointmentFail({ error: err }));
        }

        const errorMessage = getAlertErrorMessage(err, GLOBAL_CONSTANT.errorMessage);
        return this.alertService.openRetryPopup(errorMessage).pipe(
            map((shouldRetry: boolean) => shouldRetry
                ? new fromActions.EditSalesAppointment({
                    ...action.payload,
                    retryAttempt: attempt + 1
                })
                : new fromActions.EditSalesAppointmentFail({ error: err }))
        );
    }
}
